import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./E3Button.module.css";
import { useBoundStore } from "e3-common/store/useBoundStore";
import { EXIST } from "e3-common/constants/common";
import useMessageBox from "e3-common/hooks/useMessageBox";
import { property } from "lodash";

/**
 * E3Button
 * @type React.FC<E3ButtonPropTypes>
 */

const E3Button = React.forwardRef((props, ref) => {
  const { alert } = useMessageBox();
  const menuId = useBoundStore((state) => state.menu.selectedMenu.menuId);
  const authBtnList = useBoundStore((state) => state.authBtn.authBtnList);

  const [showTooltip, setShowTooltip] = useState(false);
  const excludeChk = useRef(0);

  Object.keys(props).forEach((p) => {
    if (!Object.keys(E3ButtonPropTypes).includes(p)) {
      console.error(`E3Button: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /**
   * E3Button Component의 State
   */
  const [currState, setCurrState] = useState({
    label: props.label,
    buttonType: props.buttonType,
    colorType: props.colorType,
    size: props.size,
    iconType: props.iconType,
    disabled: props.disabled,
    visible: props.visible,
    style: props.style,
    [EXIST]: props[EXIST],
  });

  const _updateState = (stateName, newValue) => {
    // currState에 있는 값만 업데이트 가능
    if (Object.keys(currState).includes(stateName)) {
      setCurrState((prev) => ({
        ...prev,
        [stateName]: newValue,
      }));
    } else {
      console.error(
        "State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
      );
    }
  };

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    refButton,
    updateState(stateName, newValue) {
      _updateState(stateName, newValue);
    },

    runClick() {
      props.onClick(props.gridIndex);
    },

    enable() {
      _updateState("disabled", false);
    },

    disable() {
      _updateState("disabled", true);
    },
    visible(value) {
      _updateState("visible", value);
    },
  }));

  const refButton = useRef();

  const _makeClassnames = ({ buttonType, colorType, size, iconType }) => {
    const classNames = [];
    classNames.push(styles.btn);

    switch (buttonType) {
      case "default":
        break;
      case "close":
        classNames.push(styles.closeBtn);
        break;
      case "search":
        excludeChk.current += 1;
        classNames.push(styles.searchBtn);
        break;
      case "init":
        classNames.push(styles.initBtn);
        break;
    }

    if (buttonType === "default") {
      switch (colorType) {
        case "default":
          classNames.push(styles.colorDefault);
          break;
        case "default-outline":
          classNames.push(styles.colorDefaultOutline);
          break;
        case "main":
          excludeChk.current += 1;
          classNames.push(styles.colorMain);
          break;
        case "main-outline":
          classNames.push(styles.colorRedMain);
          break;
        case "sub1":
          classNames.push(styles.colorSub1);
          break;
        case "sub1-outline":
          classNames.push(styles.colorSub1Outline);
          break;
        case "sub2":
          excludeChk.current += 1;
          classNames.push(styles.colorSub2);
          break;
        case "sub2-outline":
          classNames.push(styles.colorSub2Outline);
          break;
        case "sub3":
          excludeChk.current += 1;
          classNames.push(styles.colorSub3);
          break;
        case "sub3-outline":
          classNames.push(styles.colorSub3Outline);
          break;
        case "sub4":
          classNames.push(styles.colorSub4);
          break;
        case "sub5":
          classNames.push(styles.colorSub5);
          break;
        case "sub6":
          classNames.push(styles.colorSub6Line);
          break;
        case "sub7Green":
          classNames.push(styles.sub7GreenLine);
          break;
        case "pageOn":
          classNames.push(styles.pageOn);
          break;
        case "pageDefault":
          classNames.push(styles.pageDefault);
          break;
        case "prevList":
          classNames.push(styles.prevList);
          break;
        case "colorNoBorder":
          classNames.push(styles.colorNoBorder);
          break;
        case "roundRed":
          classNames.push(styles.roundRed);
          break;
      }
    }

    switch (size) {
      case "default":
        break;
      case "small":
        classNames.push(styles.small);
        break;
      case "middle":
        classNames.push(styles.middle);
        break;
      case "large":
        classNames.push(styles.large);
        break;
    }

    if (iconType) {
      classNames.push(styles.icon);
      classNames.push(styles[iconType]);
    }

    return classNames.join(" ");
  };

  const _onClick = (e) => {
    if (!props.onClick && (props.colorType || props.iconType)) {
      alert(
        "클릭이벤트 없음 - 버튼속성",
        [
          "label: ",
          props.label?.toString() ?? "",
          "\nbuttonType: ",
          props.buttonType?.toString() ?? "",
          "\ncolorType: ",
          props.colorType?.toString() ?? "",
          "\nsize: ",
          props.size?.toString() ?? "",
          "\niconType: ",
          props.iconType?.toString() ?? "",
        ]
          .join("")
          .replace(/\n/g, "<br />")
      );
      return;
    }

    if (!props.onClick && !!(props.colorType || props.iconType)) {
      alert("Error", "지정된 클릭 이벤트가 없습니다");
      return;
    }

    if (!props.checkAuth) {
      props.onClick(e, props.gridIndex);
      return;
    }
    const authBtn = authBtnList
      ?.filter((x) => x.menuId === menuId || x.menuId === "AUTH_FIX")
      ?.filter((x) => x.btnType === props.authBtn);
    if (authBtn.length) {
      props.onClick(e, props.gridIndex);
    } else {
      if (props.authBtn === "LOGIN" || props.authBtn === "CLOSE") {
        props.onClick(e, props.gridIndex);
      } else {
        alert("Error", "버튼 권한이 없습니다.");
      }
    }
  };

  const _onMouseOver = (e) => {
    if (!excludeChk.current)
      e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.05)";
    if (props.onMouseOver) {
      props.onMouseOver(e);
    }
  };

  const _onMouseOut = (e) => {
    if (!excludeChk.current) e.currentTarget.style.backgroundColor = "";
    if (props.onMouseOut) {
      props.onMouseOut(e);
    }
  };

  const _onMouseEnter = (e) => {
    setShowTooltip(true);
    if (props.onMouseEnter) {
      props.onMouseEnter(e);
    }
  };

  const _onMouseLeave = (e) => {
    setShowTooltip(false);
    if (props.onMouseLeave) {
      props.onMouseLeave(e);
    }
  };

  return currState.visible && props[EXIST] ? (
    <div className={`${styles.container}`} style={props.styleContainer ?? {}}>
      <button
        ref={refButton}
        type="button"
        className={`${_makeClassnames(currState)} ${props.className ?? ""}`}
        onClick={_onClick}
        onMouseOver={_onMouseOver}
        onMouseOut={_onMouseOut}
        onMouseEnter={_onMouseEnter}
        onMouseLeave={_onMouseLeave}
        disabled={currState.disabled}
        style={currState.style}
      >
        {currState.label ? <span>{currState.label}</span> : null}
      </button>
      {showTooltip && props.tooltip && (
        <div className={`${styles.tooltip}`}>{props.tooltip}</div>
      )}
    </div>
  ) : null;
});

const E3ButtonPropTypes = {
  /**
   * label: 버튼 텍스트
   * updateState('label', string) 으로 수정 가능
   */
  label: PropTypes.string,
  /**
   * buttonType: default, close, search 중 한가지
   * updateState('buttonType', string) 으로 수정 가능
   */
  buttonType: PropTypes.oneOf(["default", "close", "search"]),
  /**
   * colorType: default, main, sub1, sub2 중 한가지
   * updateState('colorType', string) 으로 수정 가능
   */
  colorType: PropTypes.oneOf([
    "prevList",
    "default",
    "pageDefault",
    "pageOn",
    "colorNoBorder",
    "main",
    "sub1",
    "sub2",
    "sub3",
    "sub4",
    "sub5",
    "sub6",
    "default-outline",
    "main-outline",
    "sub1-outline",
    "sub2-outline",
    "sub3-outline",
    "sub7Green",
  ]),
  /**
   * size: default, small, middle, large 중 한가지
   * updateState('size', string) 으로 수정 가능
   */
  size: PropTypes.oneOf(["default", "small", "middle", "large"]),
  /**
   * iconType: 아이콘. E3Button.module.css 에서 가용한 아이콘 참고
   * updateState('iconType', string) 으로 수정 가능
   */
  iconType: PropTypes.string,
  /**
   * onClick: 클릭 이벤트
   */
  onClick: PropTypes.func,
  /**
   * mouseover: 마우스 오버 이벤트
   */
  onMouseOver: PropTypes.func,
  /**
   * mouseout: 마우스 아웃 이벤트
   */
  onMouseOut: PropTypes.func,
  /**
   * mouseEnter: 마우스 오버 이벤트
   */
  onMouseEnter: PropTypes.func,
  /**
   * mouseLeave: 마우스 아웃 이벤트
   */
  onMouseLeave: PropTypes.func,

  /**
   * disabled: 비활성화
   * 기본값 false
   * updateState('disabled', bool) 함수로 수정 가능
   */
  disabled: PropTypes.bool,
  visible: PropTypes.bool,

  /**
   * authBtn: 버튼 권한
   */
  authBtn: PropTypes.string,
  /**
   * checkAuth: 버튼 권한 사용 여부
   */
  checkAuth: PropTypes.bool,
  /**
   * style 적용
   */
  style: PropTypes.object,
  /**
   * use the grid Index, when using the E3Button in grid(E3Table)
   */
  gridIndex: PropTypes.object,
  /**
   * className 적용
   */
  className: PropTypes.string,
  /**
   * exist button
   */
  [EXIST]: PropTypes.bool,
  /**
   * toolTip 적용
  */
 tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
 /**
  * styleContainer 적용
  */
 styleContainer: PropTypes.object,
};

E3Button.propTypes = E3ButtonPropTypes;
E3Button.defaultProps = {
  label: "",
  buttonType: "default",
  colorType: "default",
  size: "default",
  iconType: null,
  onClick: null,
  onMouseOver: null,
  onMouseOut: null,
  onMouseEnter: null,
  onMouseLeave: null,
  disabled: false,
  visible: true,
  authBtn: "noneBtn",
  checkAuth: true,
  style: null,
  gridIndex: { row: -1, col: -1, binding: "" },
  className: "",
  [EXIST]: true,
  tooltip: "",
  styleContainer: null,
};
E3Button.displayName = "E3Button";
export default E3Button;
